import React, { useMemo, useRef, useState } from "react";
import { Drawer, Row, Col, Button, Form, Divider, notification } from "antd";
import styles from "./ScheduleGrocery.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import drawerStyles from "../../DashBoard/TripDetails/TripDetailsDrawer.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import dividerStyles from "../../Shared/Styles/DividerStyles.module.scss";
import ProfileAddress from "../../DashBoard/MemberProfile/ProfileForm/ProfileAddress";
import formStyles from "../../DashBoard/MemberProfile/MemberProfile.module.scss";
import {
  formatAddress,
  getServiceUserDetails,
  validateAddressObject,
} from "../../../helpers/personHelpers";
import {
  Address,
  AddressRef,
  UspsAddresses,
  defaultDeliveryAddress,
  defaultSelectedAddress,
} from "../../../types/LocationTypes";
import UspsAddressModal from "../../CreatePerson/Modals/UspsAddressModal";
import { useGroceryCtx } from "../../../context/GroceryContext";
import { useParams } from "react-router-dom";
import { useDashboardCtx } from "../../../context/DashboardContext";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import ChangeDeliveryAddressWarningModal from "../Modals/ChangeDeliveryAddressWarningModal";

type Props = {
  cartItemsCount: number;
  isDrawerVisible: boolean;
  setIsDrawerVisible: Function;
};

function DeliveryAddressDrawer({
  cartItemsCount,
  isDrawerVisible,
  setIsDrawerVisible,
}: Props) {
  const [shouldDisableBtn, setShouldDisableBtn] = useState<boolean>(true);
  const [isUspsAddressModalVisible, setIsUspsAddressModalVisible] = useState<
    boolean
  >(false);
  const [
    isDeliveryAddressChangeWarningModalVisible,
    setIsDeliveryAddressChangeWarningModalVisible,
  ] = useState<boolean>(false);

  const addressRef = useRef<AddressRef>(null);
  const uspsAddresses = useRef<UspsAddresses>({
    deliveryAddress: null,
  });

  const { personId } = useParams<{ personId: string }>();

  const { selectedPersonIdCtx } = useDashboardCtx();
  const [selectedPersonId] = selectedPersonIdCtx;

  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;

  const profileHomeAddress = useMemo(() => {
    return getServiceUserDetails(
      Number(selectedPersonId) || Number(personId),
      serviceData
    )?.personAttributes.homeAddress;
  }, [selectedPersonId, personId, serviceData]);

  const { deliveryAddressCtx } = useGroceryCtx();
  const [, setDeliveryAddress] = deliveryAddressCtx;

  const [form] = Form.useForm();

  function handleCloseDrawer() {
    addressRef?.current?.handleResetAddressLookup();
    setIsDrawerVisible(false);
  }

  function handleUpdateDeliveryAddress() {
    setDeliveryAddress((deliveryAddress: Address) => ({
      ...form.getFieldValue("deliveryAddress"),
      organizationId: deliveryAddress?.organizationId,
    }));
    handleCloseDrawer();
  }

  function handleSubmitForm() {
    if (cartItemsCount) {
      setIsDeliveryAddressChangeWarningModalVisible(true);
    } else {
      handleUpdateDeliveryAddress();
    }
  }

  async function handleUspsAndSubmitForm() {
    try {
      const deliveryAddressState = {
        isManualEntry: !!addressRef.current?.isEnterManualAddress,
        validateUsps: addressRef.current?.validateManualAddress,
      };

      if (
        deliveryAddressState.isManualEntry &&
        deliveryAddressState.validateUsps
      ) {
        const result = await deliveryAddressState.validateUsps();
        const formattedUspsAddress = formatAddress(result.uspsAddress);
        uspsAddresses.current = {
          deliveryAddress: formattedUspsAddress,
        };

        if (result.isValid) {
          setIsUspsAddressModalVisible(true);
        }
      } else {
        handleSubmitForm();
      }
    } catch (error) {
      notification.error({
        message: "Failed USPS Validation",
      });
    }
  }

  function handleValuesChange() {
    validateForm();
  }

  function validateForm() {
    const deliveryAddress = form.getFieldValue("deliveryAddress");

    const isValidDeliveryAddress = validateAddressObject(
      deliveryAddress || defaultDeliveryAddress
    );

    isValidDeliveryAddress
      ? setShouldDisableBtn(false)
      : setShouldDisableBtn(true);
  }

  return (
    <>
      <Drawer
        title={<Row className={textStyles.normalText20}>Edit Address</Row>}
        placement="right"
        className={drawerStyles.drawerContainer}
        visible={isDrawerVisible}
        onClose={handleCloseDrawer}
      >
        <Divider className={dividerStyles.solidBrandingDivider} />

        <Form
          form={form}
          name="editDeliveryAddress"
          labelCol={{ span: 24 }}
          onFinish={handleUspsAndSubmitForm}
          className={`${formStyles.editProfileForm} ${styles.padV32H16}`}
          onValuesChange={handleValuesChange}
          requiredMark={false}
        >
          <Row gutter={[0, 8]}>
            <Col span={24} className={textStyles.boldText16}>
              Delivery address
            </Col>
            <Col span={24}>
              <ProfileAddress
                ref={addressRef}
                form={form}
                addressType={"deliveryAddress"}
                validateAndCheckForm={validateForm}
                shouldValidateZip={true}
                isCaregiverProfile={false}
              />
            </Col>
          </Row>

          <Row gutter={[0, 8]}>
            <Col span={24} className={textStyles.boldText16}>
              Current home address
            </Col>
            <Col span={24} className={textStyles.secondaryNormalText14}>
              <Row>{profileHomeAddress?.address1}</Row>
              <Row>{profileHomeAddress?.address2}</Row>
              <Row>
                {profileHomeAddress?.city}, {profileHomeAddress?.state}{" "}
                {profileHomeAddress?.zip}
              </Row>
            </Col>
          </Row>

          <Row
            justify="end"
            className={buttonStyles.drawerFooterBtnsContainer}
            gutter={[16, 16]}
          >
            <Col>
              <Button
                className={`${buttonStyles.defaultWhiteBtn} ${textStyles.normalText16}`}
                onClick={handleCloseDrawer}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                htmlType="submit"
                className={`${buttonStyles.primaryBlueBtn} ${
                  textStyles.normalText16
                } ${shouldDisableBtn && buttonStyles.disabledBtn}`}
                disabled={shouldDisableBtn}
              >
                Confirm address
              </Button>
            </Col>
          </Row>
        </Form>
      </Drawer>

      <UspsAddressModal
        form={form}
        uspsAddresses={uspsAddresses.current}
        visible={isUspsAddressModalVisible}
        setVisibility={setIsUspsAddressModalVisible}
        handleSubmitForm={handleSubmitForm}
      />

      {isDeliveryAddressChangeWarningModalVisible && (
        <ChangeDeliveryAddressWarningModal
          personId={Number(personId)}
          isVisible={isDeliveryAddressChangeWarningModalVisible}
          setVisibility={setIsDeliveryAddressChangeWarningModalVisible}
          handleUpdateDeliveryAddress={handleUpdateDeliveryAddress}
        />
      )}
    </>
  );
}

export default DeliveryAddressDrawer;
